import { findAllDutyRequests, getDashboardStats, getInquiries, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  TitleContainer,
  Tooltip,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ko";
import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  FileText,
  MessageSquare,
  RefreshCw,
  Sparkles,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { AnalyticsReportLinks } from "../analytics/AnalyticsReports";

dayjs.extend(relativeTime);
dayjs.locale("ko");

interface StatCardProps {
  title: string;
  value: number | string;
  unit?: string;
  icon: React.ReactNode;
  color: string;
  subtitle?: React.ReactNode;
}

function StatCard({ title, value, unit, icon, color, subtitle }: StatCardProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="xs" c="dimmed" fw={700} style={{ textTransform: "uppercase" }}>
            {title}
          </Text>
          <Group align="baseline" gap={4} mt={4}>
            <Title order={2} c={`${color}.7`}>
              {typeof value === "number" ? value.toLocaleString() : value}
            </Title>
            {unit && (
              <Text size="sm" fw={600} c="dimmed">
                {unit}
              </Text>
            )}
          </Group>
        </Stack>
        <Paper
          w={42}
          h={42}
          radius="md"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `var(--mantine-color-${color}-light)`,
            color: `var(--mantine-color-${color}-filled)`,
          }}
        >
          {icon}
        </Paper>
      </Group>

      {subtitle && <BoxSubtitle>{subtitle}</BoxSubtitle>}
    </Card>
  );
}

function BoxSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <Group gap="xs" mt="xs">
      {children}
    </Group>
  );
}

export function HomePage() {
  const navigate = useNavigate();

  // 대시보드 요약 통계
  const {
    data: stats,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
    refetch: refetchStats,
  } = useQuery({
    queryKey: queryKeys.dashboard.statistics,
    queryFn: () => getDashboardStats(),
  });

  // 대기 중인 근무 요청 목록 (최신 5건)
  const { data: dutyRequestsData, isLoading: isDutyRequestsLoading } = useQuery({
    queryKey: queryKeys.dutyRequests.list({ page: 1, limit: 5, status: "PENDING" }),
    queryFn: () => findAllDutyRequests({ page: 1, limit: 5, status: "PENDING" }),
  });

  // 미답변 문의사항 목록 (최신 5건)
  const { data: inquiriesData, isLoading: isInquiriesLoading } = useQuery({
    queryKey: queryKeys.inquiries.adminList(1, 5, "OPEN"),
    queryFn: () => getInquiries({ page: 1, limit: 5, status: "OPEN" }),
  });

  if (isStatsLoading) {
    return (
      <TitleContainer title="대시보드" description="실시간 통계 현황">
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      </TitleContainer>
    );
  }

  const attendanceRate = stats
    ? Math.round((stats.activeAttendanceToday / Math.max(stats.totalUsers, 1)) * 100)
    : 0;

  const pendingDutyRequestsList = dutyRequestsData?.list || [];
  const pendingInquiriesList = inquiriesData?.list || [];

  return (
    <TitleContainer
      title={
        <Group justify="space-between" align="center" style={{ width: "100%" }}>
          <div>
            <Title order={3}>서비스 운영 대시보드</Title>
            <Text size="xs" c="dimmed">
              실시간 핵심 지표 현황과 처리가 필요한 대기 업무 목록입니다.
            </Text>
          </div>
          <Tooltip label="새로고침">
            <ActionIcon
              variant="default"
              size="input-xs"
              radius="md"
              onClick={() => refetchStats()}
              loading={isStatsFetching}
            >
              <RefreshCw size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
    >
      <Stack gap="lg">
        <AnalyticsReportLinks />
        {/* 1. 상단 주요 지표 카드 */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <StatCard
            title="전체 가입자"
            value={stats?.totalUsers || 0}
            unit="명"
            icon={<Users size={22} />}
            color="blue"
            subtitle={
              <Badge color="teal" variant="light" size="sm" leftSection={<TrendingUp size={12} />}>
                오늘 +{stats?.todaysStats.newUsers || 0}명
              </Badge>
            }
          />
          <StatCard
            title="활성 병원"
            value={stats?.totalCompanies || 0}
            unit="곳"
            icon={<Building2 size={22} />}
            color="teal"
            subtitle={
              <Text size="xs" c="dimmed">
                등록된 근무 유형 {stats?.totalWorkShifts || 0}개
              </Text>
            }
          />
          <StatCard
            title="오늘 활성 출근"
            value={stats?.activeAttendanceToday || 0}
            unit="명"
            icon={<UserCheck size={22} />}
            color="indigo"
            subtitle={
              <Badge color="indigo" variant="light" size="sm">
                전체 가입자 대비 {attendanceRate}%
              </Badge>
            }
          />
          <StatCard
            title="이번 달 근무 배정"
            value={stats?.totalDutiesThisMonth || 0}
            unit="개"
            icon={<Calendar size={22} />}
            color="orange"
            subtitle={
              <Text size="xs" c="dimmed">
                이번 달 편성된 듀티 슬롯
              </Text>
            }
          />
        </SimpleGrid>

        {/* 2. 오늘 현황 & 빠른 통계 바로가기 */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Group justify="space-between" mb="md">
                <Title order={4}>오늘의 운영 현황</Title>
                <Text size="xs" c="dimmed">
                  {dayjs().format("YYYY년 M월 D일")} 기준
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <Paper p="md" radius="md" withBorder bg="blue.0">
                  <Group gap="xs" mb="xs">
                    <UserCheck size={18} color="var(--mantine-color-blue-7)" />
                    <Text size="xs" fw={700} c="blue.9">
                      현재 출근 중
                    </Text>
                  </Group>
                  <Text size="xl" fw={800} c="blue.8">
                    {(stats?.activeAttendanceToday || 0).toLocaleString()}명
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    실시간 근무 현황
                  </Text>
                </Paper>

                <Paper p="md" radius="md" withBorder bg="teal.0">
                  <Group gap="xs" mb="xs">
                    <FileText size={18} color="var(--mantine-color-teal-7)" />
                    <Text size="xs" fw={700} c="teal.9">
                      오늘 신규 요청
                    </Text>
                  </Group>
                  <Text size="xl" fw={800} c="teal.8">
                    {(stats?.todaysStats.newDutyRequests || 0).toLocaleString()}건
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    근무 생성 접수
                  </Text>
                </Paper>

                <Paper p="md" radius="md" withBorder bg="violet.0">
                  <Group gap="xs" mb="xs">
                    <MessageSquare size={18} color="var(--mantine-color-violet-7)" />
                    <Text size="xs" fw={700} c="violet.9">
                      오늘 답변 완료
                    </Text>
                  </Group>
                  <Text size="xl" fw={800} c="violet.8">
                    {(stats?.todaysStats.resolvedInquiries || 0).toLocaleString()}건
                  </Text>
                  <Text size="xs" c="dimmed" mt={2}>
                    문의 처리 완료
                  </Text>
                </Paper>
              </SimpleGrid>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
              <Title order={4} mb="sm">
                통계 분석 바로가기
              </Title>
              <Stack gap="xs">
                <Paper
                  component={Link}
                  to="/statistics/user-trends"
                  p="xs"
                  radius="md"
                  withBorder
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textDecoration: "none",
                    transition: "background-color 0.15s ease",
                  }}
                  className="hover-card"
                >
                  <Group gap="xs">
                    <ThemeIcon color="teal" variant="light" size="md" radius="md">
                      <Users size={16} />
                    </ThemeIcon>
                    <div>
                      <Text size="xs" fw={700} c="dark">
                        사용자 성장·활동 통계
                      </Text>
                      <Text size="9px" c="dimmed">
                        가입자 추이 및 출퇴근/원티드 활동 분석
                      </Text>
                    </div>
                  </Group>
                  <ChevronRight size={14} color="gray" />
                </Paper>

                <Paper
                  component={Link}
                  to="/statistics/duty-requests"
                  p="xs"
                  radius="md"
                  withBorder
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textDecoration: "none",
                  }}
                >
                  <Group gap="xs">
                    <ThemeIcon color="indigo" variant="light" size="md" radius="md">
                      <ClipboardCheck size={16} />
                    </ThemeIcon>
                    <div>
                      <Text size="xs" fw={700} c="dark">
                        Duty 요청·응답률 통계
                      </Text>
                      <Text size="9px" c="dimmed">
                        병원별 근무표 요청 건수 및 응답률 분석
                      </Text>
                    </div>
                  </Group>
                  <ChevronRight size={14} color="gray" />
                </Paper>

                <Paper
                  component={Link}
                  to="/statistics/duty-generation"
                  p="xs"
                  radius="md"
                  withBorder
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textDecoration: "none",
                  }}
                >
                  <Group gap="xs">
                    <ThemeIcon color="blue" variant="light" size="md" radius="md">
                      <Sparkles size={16} />
                    </ThemeIcon>
                    <div>
                      <Text size="xs" fw={700} c="dark">
                        Duty 자동 생성 통계
                      </Text>
                      <Text size="9px" c="dimmed">
                        서버 기록 기반 AI 근무표 자동 생성 현황
                      </Text>
                    </div>
                  </Group>
                  <ChevronRight size={14} color="gray" />
                </Paper>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* 3. 대기 중인 항목 (근무 요청 및 미답변 문의사항 상세 목록) */}
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          {/* 3-1. 대기 중인 근무 요청 */}
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <ThemeIcon color="yellow" variant="light" size="md" radius="md">
                  <ClipboardList size={18} />
                </ThemeIcon>
                <Title order={4}>대기 중인 근무 요청</Title>
                <Badge size="md" color="yellow" variant="filled">
                  {stats?.pendingDutyRequests || 0}건 대기
                </Badge>
              </Group>
              <Button
                component={Link}
                to="/duty-requests"
                variant="subtle"
                color="blue"
                size="xs"
                rightSection={<ArrowRight size={13} />}
              >
                전체 보기
              </Button>
            </Group>

            {isDutyRequestsLoading ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : pendingDutyRequestsList.length > 0 ? (
              <Stack gap="xs">
                {pendingDutyRequestsList.map((req) => (
                  <Paper
                    key={req.id}
                    p="sm"
                    radius="md"
                    withBorder
                    style={{
                      cursor: "pointer",
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                    }}
                    onClick={() => navigate(`/duty-requests/${req.id}`)}
                  >
                    <Group justify="space-between" align="center">
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs">
                          {req.team?.company?.name && (
                            <Badge size="xs" variant="light" color="blue">
                              {req.team.company.name}
                            </Badge>
                          )}
                          <Text size="sm" fw={700} truncate>
                            {req.nurse?.user?.name || "간호사"} ({req.team?.name || "부서"})
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          근무 기간: {dayjs(req.startDate).format("YYYY.MM.DD")} ~{" "}
                          {dayjs(req.endDate).format("YYYY.MM.DD")}
                        </Text>
                        {req.memo && (
                          <Text size="xs" c="dark.3" lineClamp={1}>
                            메모: {req.memo}
                          </Text>
                        )}
                      </Stack>

                      <Group gap="xs">
                        <Text size="xs" c="dimmed">
                          {dayjs(req.createdAt).fromNow()}
                        </Text>
                        <ChevronRight size={16} color="gray" />
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper p="xl" radius="md" withBorder bg="gray.0" style={{ textAlign: "center" }}>
                <CheckCircle2
                  size={28}
                  color="var(--mantine-color-teal-6)"
                  style={{ margin: "0 auto 8px" }}
                />
                <Text size="sm" fw={600} c="dimmed">
                  현재 대기 중인 근무 요청이 없습니다.
                </Text>
                <Text size="xs" c="dimmed">
                  모든 요청이 완료되었습니다.
                </Text>
              </Paper>
            )}
          </Card>

          {/* 3-2. 미답변 문의사항 */}
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <ThemeIcon color="red" variant="light" size="md" radius="md">
                  <MessageSquare size={18} />
                </ThemeIcon>
                <Title order={4}>미답변 문의사항</Title>
                <Badge size="md" color="red" variant="filled">
                  {stats?.pendingInquiries || 0}건 대기
                </Badge>
              </Group>
              <Button
                component={Link}
                to="/inquiries"
                variant="subtle"
                color="blue"
                size="xs"
                rightSection={<ArrowRight size={13} />}
              >
                전체 보기
              </Button>
            </Group>

            {isInquiriesLoading ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : pendingInquiriesList.length > 0 ? (
              <Stack gap="xs">
                {pendingInquiriesList.map((inq) => (
                  <Paper
                    key={inq.id}
                    p="sm"
                    radius="md"
                    withBorder
                    style={{
                      cursor: "pointer",
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                    }}
                    onClick={() => navigate(`/inquiries/${inq.id}`)}
                  >
                    <Group justify="space-between" align="center">
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs">
                          <Badge size="xs" color="red" variant="light">
                            답변대기
                          </Badge>
                          <Text size="sm" fw={700} truncate>
                            {inq.title}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          작성자: {inq.user?.name || inq.user?.email || "사용자"} ·{" "}
                          {dayjs(inq.createdAt).format("YYYY.MM.DD HH:mm")}
                        </Text>
                      </Stack>

                      <Group gap="xs">
                        <Text size="xs" c="dimmed">
                          {dayjs(inq.createdAt).fromNow()}
                        </Text>
                        <ChevronRight size={16} color="gray" />
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper p="xl" radius="md" withBorder bg="gray.0" style={{ textAlign: "center" }}>
                <CheckCircle2
                  size={28}
                  color="var(--mantine-color-teal-6)"
                  style={{ margin: "0 auto 8px" }}
                />
                <Text size="sm" fw={600} c="dimmed">
                  현재 미답변 문의사항이 없습니다.
                </Text>
                <Text size="xs" c="dimmed">
                  모든 문의에 답변이 완료되었습니다.
                </Text>
              </Paper>
            )}
          </Card>
        </SimpleGrid>
      </Stack>
    </TitleContainer>
  );
}
